import {Component, computed, DestroyRef, inject, input, output, signal} from '@angular/core';
import {DatePipe, NgTemplateOutlet} from '@angular/common';
import {Card} from 'primeng/card';
import {Button} from 'primeng/button';
import {Tooltip} from 'primeng/tooltip';
import {TaTemplateDetails} from '../../../api';
import {itineraryForTraveller, primaryTravellerId, sortedItems} from '../../../datasources/template.datasource';
import {TaSubmissionDatasource} from '../../../datasources/ta-submission.datasource';
import {FULL_DETAILS_MIN_WIDTH} from '../ta-info-card/ta-info-card.component';

/** Side panel for the My TA Templates tab, styled after ta-info-card. */
@Component({
  selector: 'template-summary-card',
  imports: [
    DatePipe,
    NgTemplateOutlet,
    Card,
    Button,
    Tooltip,
  ],
  templateUrl: './template-summary-card.html',
})
export class TemplateSummaryCard {
  private taSubmissionDatasource = inject(TaSubmissionDatasource);

  template = input<TaTemplateDetails | undefined>();
  createDisabled = input<boolean>(false);
  createDisabledMessage = input<string>('');
  createTa = output<void>();
  editTemplate = output<void>();

  private viewportFitsDetails = signal<boolean>(window.matchMedia(FULL_DETAILS_MIN_WIDTH).matches);
  // Set by the toggle; cleared when the viewport crosses the breakpoint.
  private detailsOverride = signal<boolean | null>(null);

  showDetails = computed<boolean>(() => this.detailsOverride() ?? this.viewportFitsDetails());
  toggleLabel = computed<string>(() => this.showDetails() ? 'Hide template summary' : 'Show template summary');

  secondaryTravellers = computed<string>(() => {
    const primaryUserId = this.template()?.primaryTraveller?.id;
    return (this.template()?.travellers ?? [])
      .filter(t => t.details?.id !== primaryUserId)
      .map(t => t.details?.name)
      .filter(Boolean)
      .join(', ');
  });

  private primaryItems = computed(() =>
    sortedItems(itineraryForTraveller(this.template(), primaryTravellerId(this.template()))));

  // Distinct arrival locations, in day order.
  destinations = computed<string>(() => [...new Set(this.primaryItems()
    .map(item => this.taSubmissionDatasource.findLocation(item.endLocationId ?? '', item.endLocationNameId ?? '')?.name)
    .filter((name): name is string => !!name))].join(', '));

  tripDuration = computed<string>(() => {
    const days = this.primaryItems().length;
    if (days === 0) return '';
    const nights = days - 1;
    return `${days} day${days === 1 ? '' : 's'} (${nights} night${nights === 1 ? '' : 's'})`;
  });

  constructor() {
    const viewport = window.matchMedia(FULL_DETAILS_MIN_WIDTH);
    const onViewportChange = (event: MediaQueryListEvent) => {
      this.viewportFitsDetails.set(event.matches);
      this.detailsOverride.set(null);
    };
    viewport.addEventListener('change', onViewportChange);
    inject(DestroyRef).onDestroy(() => viewport.removeEventListener('change', onViewportChange));
  }

  toggleDetails(): void {
    this.detailsOverride.set(!this.showDetails());
  }
}
